import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';
import FormModalHost from './FormModalHost';
import PriorityProfileReminderBanner from '../Components/ui/PriorityProfileReminderBanner';

const LayoutContext = createContext(null);

export const useLayout = () => useContext(LayoutContext);

export const TABLE_KEYS = {
  users: 'users',
  clients: 'clients',
  requirements: 'requirements',
  candidates: 'candidates',
};

const MODAL_REFRESH_MAP = {
  'create-user': TABLE_KEYS.users,
  'edit-user': TABLE_KEYS.users,
  'create-client': TABLE_KEYS.clients,
  'edit-client': TABLE_KEYS.clients,
  'create-requirement': TABLE_KEYS.requirements,
  'create-tl-requirement': TABLE_KEYS.requirements,
  'upload-candidate': [TABLE_KEYS.candidates, TABLE_KEYS.requirements],
};

export const CANDIDATE_ACTIVITY_REFRESH_KEYS = [
  TABLE_KEYS.candidates,
  TABLE_KEYS.requirements,
];

function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeFormModal, setActiveFormModal] = useState(null);
  const [modalPayload, setModalPayload] = useState(null);
  const [tableRefresh, setTableRefresh] = useState({
    users: 0,
    clients: 0,
    requirements: 0,
    candidates: 0,
  });

  const triggerTableRefresh = useCallback((keys) => {
    const keyList = Array.isArray(keys) ? keys : [keys];
    const validKeys = keyList.filter(Boolean);
    if (!validKeys.length) return;
    setTableRefresh((prev) => {
      const next = { ...prev };
      validKeys.forEach((key) => {
        next[key] = (next[key] || 0) + 1;
      });
      return next;
    });
  }, []);

  const openFormModal = useCallback((key, payload = null) => {
    setModalPayload(payload);
    setActiveFormModal(key);
  }, []);

  const closeFormModal = useCallback(() => {
    setActiveFormModal(null);
    setModalPayload(null);
  }, []);

  const handleFormSuccess = useCallback(
    (message) => {
      const refreshKey = MODAL_REFRESH_MAP[activeFormModal];
      if (refreshKey) triggerTableRefresh(refreshKey);
      closeFormModal();
      return message;
    },
    [activeFormModal, triggerTableRefresh, closeFormModal]
  );

  const layoutValue = useMemo(
    () => ({
      sidebarCollapsed,
      setSidebarCollapsed,
      mobileSidebarOpen,
      setMobileSidebarOpen,
      activeFormModal,
      modalPayload,
      tableRefresh,
      openFormModal,
      closeFormModal,
      triggerTableRefresh,
      handleFormSuccess,
    }),
    [
      sidebarCollapsed,
      mobileSidebarOpen,
      activeFormModal,
      modalPayload,
      tableRefresh,
      openFormModal,
      closeFormModal,
      triggerTableRefresh,
      handleFormSuccess,
    ]
  );

  return (
    <LayoutProvider value={layoutValue}>
      <div className={`hr-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${mobileSidebarOpen ? 'mobile-sidebar-open' : ''}`}>
        <Sidebar />
        <div className="hr-shell-main">
          <AppHeader />
          <main className="hr-shell-content">
            <PriorityProfileReminderBanner />
            <Outlet />
          </main>
          <AppFooter />
        </div>
        {mobileSidebarOpen && (
          <button
            type="button"
            className="hr-sidebar-overlay"
            aria-label="Close navigation menu"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}
      </div>
      <FormModalHost />
    </LayoutProvider>
  );
}

export const LayoutProvider = ({ children, value }) => (
  <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
);

export default AppLayout;
